import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import {
  TerminalDetail,
  TerminalDetailOption
} from '@/views/dashboard/_components/virus/virus-detail/virus-detail.service'
import { TableMemoryPagination } from '@/services/table-memory-pagination'
import { AuthStatusList } from '@/views/dashboard/_type/client-item'
import { getAreaByTerminalType } from '@/services/service-permission.service'
import { ParamsClean } from '@/common/decorators/params-clean'
export interface FetchOptions {
  group_id: number // 关键词搜索
  login_status: number // 装态
  key_word: string // 关键词搜索
  system_type: number // 操作系统类型
  scan_type?: number
  range: number
  page: number // 页码
  limit: number // 每页数量
  sort: string // 排序字段名
  asc: boolean // true-正序，false-逆序
  permission_names?: string[]
  useMemory: boolean
}

export interface TrojanItem {
  device_id: string // 终端id
  group_id: string // 分组id
  group_name: string // 分组名
  computer_name: string // 计算机名
  user_mark: string // 登记用户名
  scan_status: number // 查杀进度,0-未查杀，1-任务下发中，2-查杀中, 3-已完成, 4-查杀失败
  risk_count: number // 风险项总数
  last_scan_time: string // 查杀时间
  scan_type: number // 扫描类型,1-闪电查杀,2-全盘查杀
  login_status: number // 登陆状态, 1-在线,2-离线
  system_name: string // 操作系统名
  task_id: string // 查杀记录id
  client_ip: string// 外网IP
  system_ip: string// 内网IP
  system_type: number
  /** 授权状态集合 */
  auth_status?: AuthStatusList[]
  /** 前端字段 */
  area_type?: number
}

export interface RiskFetchOptions {
  device_id: string // 终端id
  page: number // 页码
  limit: number // 每页数量
  task_id?: string // 扫描批次id
}

export interface RiskItem {
  risk_type: string // 风险类型
  risk_item: number // 风险项
  risk_name: number // 风险名
  scan_type: string // 类型
  scan_time: number // 发现时间
  handle_time: number // 处理时间
  virus_info: string // 病毒详情
}

export interface OrderOption {
  execute_type: number,
  device_list: string[],
  group_id: number,
  scan_type: number
}

export interface RemoverLogReq {
  /** 顺序,true-正序,false-逆序 */
  asc?: boolean
  /** 结束时间 */
  end_time?: number|string
  /** 搜索关键词 */
  key_word?: string
  /** 风险项 */
  risk_item?: number
  /** 风险类型 */
  risk_types?: number[]
  risk_type?: number
  /** 查杀类型 */
  scan_type?: number
  /** 处理状态，0-全部,1-未处理(包括3-处理失败),2-已处理 ,4-已信任 */
  handle_status?: number
  limit?: number
  /** 仅看未处理 */
  only_untreated?: boolean
  page?: number
  /** 排序字段 */
  sort?: string
  /** 开始时间 */
  start_time?: number |string
  /** 扫描批次id */
  task_id?: string
  device_id?: string
  /** 分组id */
  group_id?: number
}

export interface RemoverLogList {
  /** 计算机名 */
  computer_name: string
  /** 外网IP */
  client_ip: string
  /** 内网IP */
  system_ip: string
  /** 风险类型 */
  risk_type: number
  /** 风险项 */
  risk_item: number
  /** 风险名 */
  risk_name: string
  /** 查杀方式 */
  scan_type: number
  /** 发现时间 */
  scan_time: number
  /** 处理时间 */
  handle_time: number
  /** 病毒详情 */
  virus_info: any
  /** 登录状态,1-在线,2-离线 */
  login_status: number
  /** 系统类型，1-Windows，2-Linux */
  system_type: number
  /** 分组名 */
  group_name?: string
  /** 总评得分 */
  kscore?: number
  /** 用户名 */
  user_mark?: string
  handle_status?: number
}

export interface RemoverLogRes {
  list?: RemoverLogList[]
  /** 总数 */
  total?: number
}

@Injectable()
export class TrojanService extends TableMemoryPagination<FetchOptions, TrojanItem> {
  constructor (private http: Http) {
    super()
  }

  isEqual (el1: TrojanItem, el2: TrojanItem) {
    return el1.device_id === el2.device_id
  }

  fetchOperationSystemList () {
    return this.http
      .post<{
        list: [{
          system_name: string,
          count: number
        }]
      }>('terminal/system-name/stat', {})
  }

  @ParamsClean()
  getListByServer (options: Partial<FetchOptions>) {
    return this.http
      .post<{
        total: number,
        enable_total: number
        list: TrojanItem[]
      }>('safemonitor/virus-scan/stat', options).then(res => {
        res.list.forEach(v => v.area_type = getAreaByTerminalType(v.system_type))
        return res
      })
  }

  @InvokeThrottle()
  @ParamsClean()
  fetchRiskList (options: TerminalDetailOption) {
    return this.http
      .post<{
        total: number,
        list: TerminalDetail[]
      }>('/logmanage/terminal-virus/list', options)
  }

  @InvokeThrottle()
  scanTerminal (options: OrderOption) {
    return this.http
      .post('terminal-order/scan-kill', options)
  }

  @ParamsClean()
  getVirusScanRiskView (data: RemoverLogReq) {
    return this.http.post<RemoverLogRes>('/safemonitor/virus-scan/risk_view', data).then(res => {
      res.list = this.formatLog(res.list)
      return res
    })
  }
  @ParamsClean()
  getRemoverLog (data: RemoverLogReq) {
    return this.http.post<RemoverLogRes>('/logmanage/virus-scan/stat', data).then(res => {
      res.list = this.formatLog(res.list)
      return res
    })
  }

  @InvokeThrottle()
  @ParamsClean()
  getRemoverDetails (data: RemoverLogReq) {
    return this.http.post<RemoverLogRes>('/logmanage/terminal-virus/list', data).then(res => {
      res.list = this.formatLog(res.list)
      return res
    })
  }

  formatLog (list: RemoverLogList[]) {
    return list.map(item => {
      item.virus_info = JSON.parse(item.virus_info || '{}')
      item.virus_info.payload = JSON.parse(item.virus_info.payload || '[]') || []
      item.virus_info.payload.forEach((payloadItem) => {
        item.virus_info.payload[payloadItem.k] = payloadItem.v
      })
      return item
    })
  }
}

const RISK_COUNT_COLUMN = 'risk_count'
export function computedDefaultSort (query: any = {}, options: FetchOptions) {
  if (query?.sort === RISK_COUNT_COLUMN) {
    const asc = query.asc === 'true'
    options.sort = RISK_COUNT_COLUMN
    options.asc = asc
    return {
      prop: RISK_COUNT_COLUMN,
      order: asc ? 'ascending' : 'descending'
    }
  }
  return {}
}
